import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LogOut, Plus, Trash2, Edit3, Save, X, Upload, GripVertical,
  Eye, EyeOff, ArrowLeft, ImageIcon, CheckCircle2, AlertCircle, LayoutGrid, RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ROUTE_PATHS, Announcement, AnnouncementImage } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { fadeInUp } from '@/lib/motion';

type DraftAnnouncement = Omit<Announcement, 'id' | 'created_at' | 'updated_at'>;

const EMPTY_DRAFT: DraftAnnouncement = {
  title: '',
  content: '',
  images: [],
  published: false
};

export default function MemberDashboard() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const orgChartInputRef = useRef<HTMLInputElement>(null);

  const [userEmail, setUserEmail] = useState('');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<'announcements' | 'orgchart'>('announcements');

  // Org chart state
  const [orgChartUrl, setOrgChartUrl] = useState<string | null>(null);
  const [isUploadingOrgChart, setIsUploadingOrgChart] = useState(false);

  // Editor state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [draft, setDraft] = useState<DraftAnnouncement>(EMPTY_DRAFT);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // ── Fetch announcements ──
  const fetchAnnouncements = useCallback(async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('announcements_2026_04_15')
      .select('*')
      .order('updated_at', { ascending: false });
    if (error) {
      toast.error('載入公告失敗：' + error.message);
    } else if (data) {
      setAnnouncements(data.map(row => ({
        ...row,
        images: Array.isArray(row.images) ? (row.images as AnnouncementImage[]) : []
      })) as Announcement[]);
    }
    setIsLoading(false);
  }, []);

  // ── Fetch org chart ──
  const fetchOrgChart = useCallback(async () => {
    const { data } = await supabase
      .from('org_chart_settings_2026_04_15')
      .select('image_url')
      .eq('id', 1)
      .single();
    if (data) setOrgChartUrl(data.image_url ?? null);
  }, []);

  // ── Auth check ──
  useEffect(() => {
    const role = sessionStorage.getItem('hktcm_role');
    const user = sessionStorage.getItem('hktcm_user');
    if (role !== 'admin') {
      navigate(ROUTE_PATHS.MEMBER_LOGIN);
      return;
    }
    setUserEmail(user ?? 'admin');
    fetchAnnouncements();
    fetchOrgChart();
  }, [navigate, fetchAnnouncements, fetchOrgChart]);

  // ── Logout ──
  function handleLogout() {
    sessionStorage.removeItem('hktcm_role');
    sessionStorage.removeItem('hktcm_user');
    navigate(ROUTE_PATHS.MEMBER_LOGIN);
  }

  // ── Editor helpers ──
  function openNew() {
    setDraft({ ...EMPTY_DRAFT });
    setEditingId(null);
    setShowEditor(true);
  }

  function openEdit(ann: Announcement) {
    setDraft({
      title: ann.title,
      content: ann.content,
      images: ann.images.map(img => ({ ...img })),
      published: ann.published
    });
    setEditingId(ann.id);
    setShowEditor(true);
  }

  function closeEditor() {
    setShowEditor(false);
    setDraft({ ...EMPTY_DRAFT });
    setEditingId(null);
    setDragIdx(null);
    setDragOver(false);
  }

  // ── Image upload ──
  async function uploadImages(files: FileList) {
    const remaining = 8 - draft.images.length;
    if (remaining <= 0) {
      toast.error('已達到最多 8 張圖片上限');
      return;
    }
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length < files.length) {
      toast.warning(`只會上傳前 ${toUpload.length} 張（上限 8 張）`);
    }

    setIsUploading(true);
    const newImages: AnnouncementImage[] = [];

    for (const file of toUpload) {
      if (!file.type.startsWith('image/')) {
        toast.error(`「${file.name}」不是圖片格式，已跳過`);
        continue;
      }
      const ext = file.name.split('.').pop() ?? 'jpg';
      const filename = `ann_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('announcement-images')
        .upload(filename, file, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        toast.error(`上傳「${file.name}」失敗：${uploadError.message}`);
        continue;
      }

      const { data: urlData } = supabase.storage
        .from('announcement-images')
        .getPublicUrl(uploadData.path);

      newImages.push({
        url: urlData.publicUrl,
        order: draft.images.length + newImages.length,
        filename: uploadData.path
      });
    }

    if (newImages.length > 0) {
      setDraft(d => ({
        ...d,
        images: [
          ...d.images,
          ...newImages.map((img, i) => ({ ...img, order: d.images.length + i }))
        ]
      }));
      toast.success(`成功上傳 ${newImages.length} 張圖片`);
    }
    setIsUploading(false);
    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── Remove image from draft ──
  async function removeImage(idx: number) {
    const img = draft.images[idx];
    // Delete from storage (non-blocking, best-effort)
    if (img.filename) {
      supabase.storage.from('announcement-images').remove([img.filename]).catch(() => {});
    }
    setDraft(d => ({
      ...d,
      images: d.images
        .filter((_, i) => i !== idx)
        .map((item, i) => ({ ...item, order: i }))
    }));
  }

  // ── Drag reorder images ──
  function onDragStart(idx: number) { setDragIdx(idx); }

  function onDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setDraft(d => {
      const imgs = [...d.images];
      const [moved] = imgs.splice(dragIdx, 1);
      imgs.splice(idx, 0, moved);
      return { ...d, images: imgs.map((img, i) => ({ ...img, order: i })) };
    });
    setDragIdx(idx);
  }

  function onDragEnd() { setDragIdx(null); setDragOver(false); }

  // ── Save (create / update) ──
  async function handleSave() {
    if (!draft.title.trim()) { toast.error('請輸入公告主題'); return; }
    if (!draft.content.trim()) { toast.error('請輸入公告內容'); return; }

    setIsSaving(true);
    const now = new Date().toISOString();
    const payload = {
      title: draft.title.trim(),
      content: draft.content.trim(),
      images: draft.images.map((img, i) => ({ ...img, order: i })),
      published: draft.published,
      updated_at: now
    };

    let saveError = null;

    if (editingId) {
      // UPDATE existing
      const { error } = await supabase
        .from('announcements_2026_04_15')
        .update(payload)
        .eq('id', editingId);
      saveError = error;
    } else {
      // INSERT new
      const { error } = await supabase
        .from('announcements_2026_04_15')
        .insert({ ...payload, created_at: now });
      saveError = error;
    }

    setIsSaving(false);

    if (saveError) {
      toast.error('儲存失敗：' + saveError.message);
    } else {
      toast.success(editingId ? '✅ 公告已成功更新！' : '✅ 公告已成功建立！');
      closeEditor();
      await fetchAnnouncements();
    }
  }

  // ── Toggle publish status ──
  async function togglePublish(ann: Announcement) {
    const newStatus = !ann.published;
    const { error } = await supabase
      .from('announcements_2026_04_15')
      .update({ published: newStatus, updated_at: new Date().toISOString() })
      .eq('id', ann.id);

    if (error) {
      toast.error('操作失敗：' + error.message);
    } else {
      toast.success(newStatus ? '✅ 已發佈！公告即時顯示於主頁' : '⏸ 已取消發佈');
      await fetchAnnouncements();
    }
  }

  // ── Delete announcement ──
  async function deleteAnnouncement(ann: Announcement) {
    if (!confirm(`確定刪除公告「${ann.title}」？\n此操作不可復原。`)) return;

    // Delete images from storage
    const filesToDelete = ann.images
      .map(i => i.filename)
      .filter((f): f is string => typeof f === 'string' && f.length > 0);

    if (filesToDelete.length > 0) {
      const { error: storageError } = await supabase.storage
        .from('announcement-images')
        .remove(filesToDelete);
      if (storageError) {
        console.warn('部分圖片未能從 Storage 刪除：', storageError.message);
      }
    }

    const { error } = await supabase
      .from('announcements_2026_04_15')
      .delete()
      .eq('id', ann.id);

    if (error) {
      toast.error('刪除失敗：' + error.message);
    } else {
      toast.success('🗑 公告已刪除');
      await fetchAnnouncements();
    }
  }

  // ── Org chart upload ──
  async function handleOrgChartUpload(file: File) {
    setIsUploadingOrgChart(true);
    try {
      const ext = file.name.split('.').pop() ?? 'png';
      const filename = `org_chart_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('org-chart-images')
        .upload(filename, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('org-chart-images')
        .getPublicUrl(filename);

      const { error: dbError } = await supabase
        .from('org_chart_settings_2026_04_15')
        .update({ image_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', 1);
      if (dbError) throw dbError;

      setOrgChartUrl(publicUrl);
      toast.success('✅ 組織架構圖已成功上傳！');
    } catch (err: unknown) {
      toast.error('上傳失敗：' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsUploadingOrgChart(false);
      if (orgChartInputRef.current) orgChartInputRef.current.value = '';
    }
  }

  // ── Render ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">

      {/* Top bar */}
      <header className="bg-background/90 backdrop-blur-md border-b border-border px-4 py-3 sticky top-0 z-30">
        <div className="container mx-auto flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <img
              src="/images/hkcmprgu-logo-01.png"
              alt="香港中醫師權益總工會"
              className="h-9 w-auto"
            />
            <span className="text-sm font-semibold text-primary hidden sm:inline">管理員後台</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:inline">{userEmail}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2 text-destructive hover:text-destructive">
              <LogOut className="w-4 h-4" />登出
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>

          {/* Page header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">管理員後台</h1>
              <p className="text-sm text-muted-foreground mt-1">管理主頁公告及組織架構</p>
            </div>
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link to={ROUTE_PATHS.HOME}>
                <ArrowLeft className="w-4 h-4" />返回主頁
              </Link>
            </Button>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-0 mb-6 border-b border-border">
            <button
              onClick={() => setActiveTab('announcements')}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'announcements'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              最新動向管理
            </button>
            <button
              onClick={() => setActiveTab('orgchart')}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
                activeTab === 'orgchart'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />組織架構管理
            </button>
          </div>

          {/* ═══════════════════════════════════════════
              ANNOUNCEMENTS TAB
          ═══════════════════════════════════════════ */}
          {activeTab === 'announcements' && (
            <div>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-muted-foreground">
                  共 {announcements.length} 則公告，
                  <span className="text-primary font-medium">
                    {announcements.filter(a => a.published).length} 則已發佈
                  </span>
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchAnnouncements} className="gap-2">
                    <RefreshCw className="w-3.5 h-3.5" />重新整理
                  </Button>
                  <Button onClick={openNew} size="sm" className="gap-2">
                    <Plus className="w-4 h-4" />新增公告
                  </Button>
                </div>
              </div>

              {/* List */}
              {isLoading ? (
                <div className="text-center py-20">
                  <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-3" />
                  <p className="text-muted-foreground">載入中...</p>
                </div>
              ) : announcements.length === 0 ? (
                <Card className="text-center py-16 border-dashed">
                  <CardContent>
                    <ImageIcon className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">尚未有任何公告</p>
                    <Button onClick={openNew} className="gap-2">
                      <Plus className="w-4 h-4" />新增第一則公告
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {announcements.map(ann => (
                    <Card
                      key={ann.id}
                      className={`border transition-colors ${ann.published ? 'border-primary/30 bg-primary/2' : 'border-border'}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Thumbnail */}
                          {ann.images.length > 0 ? (
                            <img
                              src={ann.images[0].url}
                              alt="公告圖片"
                              className="w-16 h-14 object-cover rounded-lg flex-shrink-0 border border-border"
                            />
                          ) : (
                            <div className="w-16 h-14 bg-muted rounded-lg flex-shrink-0 flex items-center justify-center border border-border">
                              <ImageIcon className="w-5 h-5 text-muted-foreground/40" />
                            </div>
                          )}

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-semibold text-foreground truncate max-w-xs">{ann.title}</h3>
                              <Badge
                                variant={ann.published ? 'default' : 'secondary'}
                                className={`text-xs flex-shrink-0 gap-1 ${ann.published ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : ''}`}
                              >
                                {ann.published
                                  ? <><CheckCircle2 className="w-3 h-3" />已發佈</>
                                  : <><AlertCircle className="w-3 h-3" />未發佈</>
                                }
                              </Badge>
                              {ann.images.length > 0 && (
                                <span className="text-xs text-muted-foreground">{ann.images.length} 張圖片</span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-1">{ann.content}</p>
                            <p className="text-xs text-muted-foreground">
                              更新：{new Date(ann.updated_at).toLocaleString('zh-HK')}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              title={ann.published ? '取消發佈' : '立即發佈'}
                              onClick={() => togglePublish(ann)}
                              className={`gap-1.5 text-xs ${ann.published ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-muted-foreground hover:text-primary'}`}
                            >
                              {ann.published
                                ? <><Eye className="w-4 h-4" />取消發佈</>
                                : <><EyeOff className="w-4 h-4" />發佈</>
                              }
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="編輯"
                              onClick={() => openEdit(ann)}
                              className="text-muted-foreground hover:text-primary"
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="刪除"
                              onClick={() => deleteAnnouncement(ann)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════
              ORG CHART TAB
          ═══════════════════════════════════════════ */}
          {activeTab === 'orgchart' && (
            <div className="max-w-2xl">
              <Card>
                <CardHeader>
                  <CardTitle className="text-primary">組織架構圖管理</CardTitle>
                  <CardDescription>上傳第七屆理事會架構圖，建議使用高解析度圖片</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {orgChartUrl ? (
                    <div className="rounded-xl overflow-hidden border border-border">
                      <img src={orgChartUrl} alt="現有架構圖" className="w-full h-auto" />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-border rounded-xl text-muted-foreground">
                      <ImageIcon className="w-12 h-12 mb-3 opacity-30" />
                      <p>尚未上傳架構圖</p>
                    </div>
                  )}
                  <div>
                    <input
                      ref={orgChartInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleOrgChartUpload(file);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      onClick={() => orgChartInputRef.current?.click()}
                      disabled={isUploadingOrgChart}
                      className="gap-2 w-full"
                    >
                      {isUploadingOrgChart ? (
                        <><span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full inline-block" />上傳中...</>
                      ) : (
                        <><Upload className="w-4 h-4" />{orgChartUrl ? '更換架構圖' : '上傳架構圖'}</>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 text-center">支援 JPG、PNG、GIF、WebP 格式</p>
                  </div>
                  {orgChartUrl && (
                    <Button variant="outline" className="w-full gap-2" asChild>
                      <Link to={ROUTE_PATHS.ORG_CHART}>
                        <Eye className="w-4 h-4" />查看組織架構頁面
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

        </motion.div>
      </main>

      {/* ═══════════════════════════════════════════
          EDITOR MODAL (shared across tabs)
      ═══════════════════════════════════════════ */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-6 px-4">
          <Card className="w-full max-w-2xl shadow-2xl my-auto">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-primary text-xl">
                    {editingId ? '編輯公告' : '新增公告'}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    填寫公告資料，最多可上傳 8 張圖片並拖曳排序
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={closeEditor} className="flex-shrink-0">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-5">

              {/* ─── Title ─── */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 block">
                  主題 <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="輸入公告主題"
                  value={draft.title}
                  onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                  className="bg-muted/50"
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">{draft.title.length}/100</p>
              </div>

              {/* ─── Content ─── */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 block">
                  內容 <span className="text-destructive">*</span>
                </label>
                <Textarea
                  placeholder="輸入公告內容..."
                  rows={7}
                  value={draft.content}
                  onChange={e => setDraft(d => ({ ...d, content: e.target.value }))}
                  className="bg-muted/50 resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">{draft.content.length} 字</p>
              </div>

              {/* ─── Images ─── */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 block">
                  圖片（{draft.images.length} / 8）
                </label>

                {/* Drop zone */}
                <div
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                    draft.images.length >= 8
                      ? 'opacity-40 cursor-not-allowed border-border'
                      : dragOver
                        ? 'border-primary bg-primary/5 cursor-copy'
                        : 'border-border hover:border-primary/60 cursor-pointer'
                  }`}
                  onClick={() => draft.images.length < 8 && fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); if (draft.images.length < 8) setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer.files.length && draft.images.length < 8) {
                      uploadImages(e.dataTransfer.files);
                    }
                  }}
                >
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin w-6 h-6 border-3 border-primary border-t-transparent rounded-full" />
                      <p className="text-sm text-primary">圖片上傳中，請稍候...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground font-medium">
                        {draft.images.length >= 8 ? '已達上限（8 張）' : '點擊選擇或拖曳圖片至此'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">支援 JPG、PNG、GIF、WebP，可同時選多張</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={e => { if (e.target.files) uploadImages(e.target.files); }}
                />

                {/* Image preview grid */}
                {draft.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mt-4">
                    {draft.images
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map((img, idx) => (
                        <div
                          key={`${img.url}-${idx}`}
                          draggable
                          onDragStart={() => onDragStart(idx)}
                          onDragOver={e => onDragOver(e, idx)}
                          onDragEnd={onDragEnd}
                          className={`relative group rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${
                            dragIdx === idx
                              ? 'opacity-50 scale-95 border-primary shadow-lg'
                              : 'border-border hover:border-primary/60'
                          }`}
                        >
                          <img
                            src={img.url}
                            alt={`圖片 ${idx + 1}`}
                            className="w-full h-20 object-cover"
                          />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <GripVertical className="w-5 h-5 text-white" />
                          </div>
                          {/* Delete btn */}
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); removeImage(idx); }}
                            className="absolute top-1 right-1 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
                            title="移除此圖片"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {/* Order badge */}
                          <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                            {idx + 1}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                {draft.images.length > 1 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    💡 拖曳圖片可調整順序
                  </p>
                )}
              </div>

              {/* ─── Publish toggle ─── */}
              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30">
                <div>
                  <p className="text-sm font-semibold text-foreground">發佈狀態</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {draft.published
                      ? '✅ 發佈中 — 公告顯示於主頁最新動向'
                      : '⏸ 未發佈 — 儲存後不會顯示於主頁'
                    }
                  </p>
                </div>
                <Button
                  type="button"
                  variant={draft.published ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDraft(d => ({ ...d, published: !d.published }))}
                  className={`gap-2 min-w-28 ${draft.published ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                >
                  {draft.published
                    ? <><CheckCircle2 className="w-4 h-4" />已發佈</>
                    : <><EyeOff className="w-4 h-4" />未發佈</>
                  }
                </Button>
              </div>

              {/* ─── Action buttons ─── */}
              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || isUploading}
                  className="flex-1 gap-2 h-11"
                >
                  {isSaving ? (
                    <><span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" />儲存中...</>
                  ) : (
                    <><Save className="w-4 h-4" />儲存公告</>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeEditor}
                  disabled={isSaving}
                  className="gap-2 h-11 px-6"
                >
                  <X className="w-4 h-4" />取消
                </Button>
              </div>

            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
